import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Container,
  TextField,
  InputAdornment,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  ListItemIcon,
  Collapse,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import InfoIcon from '@mui/icons-material/Info';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import SchoolIcon from '@mui/icons-material/School';
import RateReviewIcon from '@mui/icons-material/RateReview';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aboutAnchor, setAboutAnchor] = useState(null);
  const [staffAnchor, setStaffAnchor] = useState(null);
  const [partnershipAnchor, setPartnershipAnchor] = useState(null);
  const [socialAnchor, setSocialAnchor] = useState(null);
  const [researchAnchor, setResearchAnchor] = useState(null);

  // Tracks which top-level section is expanded inside the mobile drawer
  const [expandedMobileSection, setExpandedMobileSection] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    setExpandedMobileSection(null);
  };

  const handleMobileSectionToggle = (label) => {
    setExpandedMobileSection((prev) => (prev === label ? null : label));
  };

  const handleMobileNavigate = (path) => {
    window.location.href = path;
    setMobileOpen(false);
    setExpandedMobileSection(null);
  };

  // About Us Submenu Items
  const aboutUsSubmenu = [
    {
      label: 'Who we are',
      path: '/about-us',
      icon: <InfoIcon fontSize="small" />,
    },
    {
      label: 'Services',
      path: '/about-us/services',
      icon: <MiscellaneousServicesIcon fontSize="small" />,
    },
    {
      label: 'Admissions',
      path: '/about-us/admissions',
      icon: <AppRegistrationIcon fontSize="small" />,
    },
    {
      label: 'Courses',
      path: '/about-us/courses',
      icon: <SchoolIcon fontSize="small" />,
    },
    {
      label: 'Testimonials',
      path: '/about-us/testimonials',
      icon: <RateReviewIcon fontSize="small" />,
    },
    {
      label: 'Universities',
      path: '/about-us/map',
      icon: <SchoolIcon fontSize="small" />,
    },
  ];

  const menuItems = [
    {
      label: 'About Us',
      anchor: aboutAnchor,
      setAnchor: setAboutAnchor,
      submenu: aboutUsSubmenu,
    },
    {
      label: 'Staff',
      anchor: staffAnchor,
      setAnchor: setStaffAnchor,
      submenu: [{ label: 'Our Faculty', path: '/faculty' }],
    },
    {
      label: 'Social Outreach',
      anchor: socialAnchor,
      setAnchor: setSocialAnchor,
      submenu: [{ label: 'Our social activities', path: '/social-outreach' }],
    },
    {
      label: 'Alumni',
      anchor: partnershipAnchor,
      setAnchor: setPartnershipAnchor,
      submenu: [{ label: 'Our Alumni', path: '/alumni' }],
    },
    {
      label: 'Research',
      anchor: researchAnchor,
      setAnchor: setResearchAnchor,
      submenu: [{ label: 'Our Research', path: '/research' }],
    },
  ];

  return (
    <>
      {/* Top Bar with Social Icons */}
      <Box
        sx={{
          backgroundColor: '#2B2B6B',
          color: 'white',
          display: { xs: 'none', md: 'block' },
          py: 1,
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {/* Social Icons - Left Side */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <IconButton size="small" sx={{ color: 'white', p: 0.5 }} href="https://www.facebook.com/collegeiaes/" target="_blank" rel="noopener noreferrer">
                <FacebookIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton size="small" sx={{ color: 'white', p: 0.5 }} href="https://www.instagram.com/collegeiaes/?hl=es" target="_blank" rel="noopener noreferrer">
                <InstagramIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton size="small" sx={{ color: 'white', p: 0.5 }} href="https://sv.linkedin.com/company/collegeiaes" target="_blank" rel="noopener noreferrer">
                <LinkedInIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Contact Info & Actions - Right Side */}
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <EmailIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                  institdevelopment@americancollege
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <PhoneIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                  7840-5400
                </Typography>
              </Box>

              <Box
                component="img"
                src="/nexus.png"
                alt="Student Access Logo"
                onClick={() => window.location.href = 'https://nexus.americancollege.edu.sv/#courses'}
                sx={{
                  height: 40,
                  width: '80px',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  '&:hover': { opacity: 0.8 },
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <AppBar
        position="sticky"
        sx={{
          backgroundColor: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            sx={{
              justifyContent: 'space-between',
              py: 1.5,
              minHeight: { xs: 64, md: 80 },
            }}
          >
            {/* Logo - Left Side */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                component="img"
                src="/Logo American College B.jpg"
                alt="American College El Salvador"
                sx={{
                  height: { xs: 50, md: 70 },
                  width: 'auto',
                  cursor: 'pointer',
                }}
                onClick={() => window.location.href = '/'}
              />
            </Box>

            {/* Desktop Menu - Center */}
            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                gap: 0.5,
                alignItems: 'center',
                flex: 1,
                justifyContent: 'center',
                ml: 4,
              }}
            >
              {menuItems.map((item) => (
                <Box key={item.label}>
                  <Button
                    endIcon={<KeyboardArrowDownIcon />}
                    onClick={(e) => item.setAnchor(e.currentTarget)}
                    sx={{
                      color: '#333',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      px: 1.5,
                      '&:hover': {
                        color: '#E31E24',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                  <Menu
                    anchorEl={item.anchor}
                    open={Boolean(item.anchor)}
                    onClose={() => item.setAnchor(null)}
                    MenuListProps={{
                      sx: { py: 1 },
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 220,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    {item.submenu?.map((subItem, index) => (
                      <MenuItem
                        key={index}
                        onClick={() => {
                          window.location.href = subItem.path;
                          item.setAnchor(null);
                        }}
                        sx={{
                          py: 1.5,
                          px: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(227,30,36,0.08)',
                            color: '#E31E24',
                          },
                        }}
                      >
                        {subItem.icon && (
                          <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                            {subItem.icon}
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={subItem.label}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            fontWeight: 500,
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              ))}
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              sx={{
                display: { xs: 'flex', lg: 'none' },
                color: '#2B2B6B',
              }}
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: 300,
            backgroundColor: 'white',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <TextField
            placeholder="Search Courses"
            size="small"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />

        <List sx={{ py: 0 }}>
          {menuItems.map((item) => {
            const isExpanded = expandedMobileSection === item.label;
            const hasSingleLink = item.submenu?.length === 1;

            return (
              <Box key={item.label}>
                <ListItemButton
                  onClick={() =>
                    hasSingleLink
                      ? handleMobileNavigate(item.submenu[0].path)
                      : handleMobileSectionToggle(item.label)
                  }
                  sx={{
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(227,30,36,0.08)',
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }}
                  />
                  {!hasSingleLink && (isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                </ListItemButton>

                {!hasSingleLink && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.submenu.map((subItem, index) => (
                        <ListItemButton
                          key={index}
                          onClick={() => handleMobileNavigate(subItem.path)}
                          sx={{
                            pl: 4,
                            py: 1.1,
                            '&:hover': {
                              backgroundColor: 'rgba(227,30,36,0.08)',
                              color: '#E31E24',
                            },
                          }}
                        >
                          {subItem.icon && (
                            <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
                              {subItem.icon}
                            </ListItemIcon>
                          )}
                          <ListItemText
                            primary={subItem.label}
                            primaryTypographyProps={{ fontSize: '0.875rem' }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}
                <Divider />
              </Box>
            );
          })}

          <ListItemButton
            onClick={() => {
              window.location.href = 'https://nexus.americancollege.edu.sv/login/index.php';
              setMobileOpen(false);
            }}
            sx={{ justifyContent: 'center', py: 2 }}
          >
            <Box
              component="img"
              src="/nexus.png"
              alt="Student Access Logo"
              sx={{ height: 35 }}
            />
          </ListItemButton>
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;